import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import classNames from 'classnames';
import { Redirect, useHistory } from 'react-router-dom';

import styles from '../../../../ProductDetails/ProductDetails.module.scss';
import supplementsEcomStyles from './SupplementsEcom.module.scss';
import { DesktopImageGallery } from '../../../../ManageSubscription/Supplements/components/ImageGallery';
import NavBar from '../../../../../components/AppBar/NavBar';
import HideChatWidget from '../../../../../components/ZendeskChat/HideChatWidget';
import FAQ from '../../../../Checkout/components/Supplements/ProductInfo/FAQ';
import {
  AllBenefits,
  getSKUForCartItem,
  PurchaseType,
  Reviews,
  skuIntegerImageMap,
  SKUOption,
  SKUType,
  urlTypeParamToSkuType,
} from '../UpsellV2/UpsellV2';
import AppPaths from '../../../../../AppPaths';
import { getFiGrowthBook } from '../../../../../lib/growthbook';
import CollarAddToBagButton from '../../../../ProductDetails/Series3CollarDetails/components/CollarAddToBagButton';
import { cartActions } from '../../../../../reducers/storeCart';
import { ReactComponent as AddIcon } from '../../../../../assets/images/icons/add.svg';
import { ReactComponent as MinusIcon } from '../../../../../assets/images/icons/minus.svg';
import Footer from '../../../../../components/Footer/Footer';
import { HideCookieConsentBanner } from '../../../../../lib/oneTrust';
import { supplementsProductIsSupportedInCountry, useLocale } from '../../../../../contexts/LocaleContext';
import { series3PlusCollarId } from '../../../../../types/Product';

export interface SupplementsEcomProps {
  isEcom: boolean;
}

const getSupplementTextMap = (t: any): Record<SKUType, { title: string; description: string }> => ({
  [SKUType.Formula]: {
    title: t('8_in_1_formula'),
    description: t('packed_with_essential_ingredients_for_comprehensive_support'),
  },
  [SKUType.HipJoint]: {
    title: t('hip_joint'),
    description: t('maintains_mobility_and_flexibility_with_glucosamine_hcl_msm_and_more'),
  },
  [SKUType.SkinCoat]: {
    title: t('skin_coat'),
    description: t('supports_vibrant_glossy_coats_with_omega_3_fatty_acids_and_more'),
  },
  [SKUType.Calming]: {
    title: t('calming'),
    description: t('promotes_relaxation_with_a_blend_of_cbd_valerian_root_and_more'),
  },
});

const EcomPurchaseContainer: React.FC<{
  quantity: number;
  setQuantity: React.Dispatch<React.SetStateAction<number>>;
  t: TFunction;
}> = ({ quantity, setQuantity, t }) => {
  return (
    <div className={classNames(supplementsEcomStyles.purchaseContainer, supplementsEcomStyles.ecomPurchaseContainer)}>
      <div className={supplementsEcomStyles.frequencyTitle}>{t('pick_your_quantity')}</div>
      <div className={supplementsEcomStyles.frequencyContainer}>
        <button
          className={classNames(supplementsEcomStyles.frequencyMinusButton, {
            [supplementsEcomStyles.frequencyMinusButtonDisabled]: quantity === 1,
          })}
          onClick={() => setQuantity(Math.max(quantity - 1, 1))}
        >
          <MinusIcon />
        </button>
        <div className={supplementsEcomStyles.frequencyValue}>{quantity}</div>
        <button
          className={classNames(supplementsEcomStyles.frequencyPlusButton, {
            [supplementsEcomStyles.frequencyPlusButtonDisabled]: quantity === 100,
          })}
          onClick={() => setQuantity(quantity + 1)}
        >
          <AddIcon />
        </button>
      </div>
    </div>
  );
};

export default function SupplementsEcom() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const [currentCarouselImage, setCurrentCarouselImage] = useState<number>(0);
  const [quantity, setQuantity] = useState<number>(1);
  const initialSkuType =
    urlTypeParamToSkuType[new URLSearchParams(window.location.search).get('type') ?? ''] ?? SKUType.Formula;
  const [selectedSKUType, setSelectedSKUType] = useState<SKUType>(initialSkuType);
  const { country, currency } = useLocale();
  const supplementTextMap = getSupplementTextMap(t);

  const supplementsEcomEnabled = getFiGrowthBook().getFeatureValue<boolean>('ecom-supplements-4-sku', false);
  if (!supplementsEcomEnabled) {
    return <Redirect to={AppPaths.NotFound} />;
  }
  if (!supplementsProductIsSupportedInCountry(country)) {
    return <Redirect to={AppPaths.Products(series3PlusCollarId)} />;
  }

  return (
    <>
      <div
        className={classNames(
          styles.productContainer,
          styles.ecomFlowRevamp,
          'ecom-flow-revamp',
          'supplements-ecom-pdp',
          {
            'ecom-flow-revamp-light':
              (selectedSKUType === SKUType.Calming && currentCarouselImage === 0) ||
              (selectedSKUType === SKUType.HipJoint && currentCarouselImage === 2),
          },
        )}
      >
        <NavBar />
        <div className={styles.galleryContainer}>
          <DesktopImageGallery
            media={[
              {
                type: 'image',
                url: `/product_images/supplements/upsell-v2/supplements-ecom-1-${skuIntegerImageMap[selectedSKUType]}.png`,
                retinaWidth: 1200,
                retinaHeight: 1200,
              },
              {
                type: 'image',
                url: `/product_images/supplements/upsell-v2/supplements-ecom-2-${skuIntegerImageMap[selectedSKUType]}.png`,
                retinaWidth: 1200,
                retinaHeight: 1200,
              },
              {
                type: 'image',
                url: `/product_images/supplements/upsell-v2/supplements-ecom-3-${skuIntegerImageMap[selectedSKUType]}.png`,
                retinaWidth: 1200,
                retinaHeight: 1200,
              },
            ]}
            onChange={(index: number) => setCurrentCarouselImage(index)}
          />
        </div>
        <div className={classNames(styles.productDetails, 'product-details')}>
          <div className={styles.productTitleContainer}>
            <div className={supplementsEcomStyles.productHeader}>{t('fi_supplements')}</div>
            <div className={supplementsEcomStyles.productTitle}>{supplementTextMap[selectedSKUType].title}</div>
            <div className={supplementsEcomStyles.productTitleDescription}>
              {supplementTextMap[selectedSKUType].description}
            </div>
            <div className={supplementsEcomStyles.productFooter}>{t('amount_per_bag', { amount: '30' })}</div>
            <br />
            <div className={supplementsEcomStyles.skuOptionSelector}>
              {Object.values(SKUType).map((skuType) => (
                <SKUOption
                  key={skuType}
                  skuType={skuType}
                  selectedSKUType={selectedSKUType}
                  onSelect={setSelectedSKUType}
                />
              ))}
            </div>
            <div className={supplementsEcomStyles.divider} />
            <EcomPurchaseContainer quantity={quantity} setQuantity={setQuantity} t={t} />
            <div className={supplementsEcomStyles.divider} />
            <div className={supplementsEcomStyles.addToBagContainer}>
              <CollarAddToBagButton
                descriptionText={null}
                isVisibleOverride={true}
                onAddToBag={() => {
                  dispatch(
                    cartActions.addSingleLineCartItem({
                      sku: getSKUForCartItem({
                        skuType: selectedSKUType,
                        eligibleForDiscount: false,
                        purchaseType: PurchaseType.OneTime,
                      }),
                      quantity: quantity,
                      currency,
                    }),
                  );
                  history.push(AppPaths.Bag);
                  return true;
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <div className={supplementsEcomStyles.benefitsContainer}>
        <AllBenefits skuType={selectedSKUType} />
      </div>
      <div className={supplementsEcomStyles.divider} />
      <div className={supplementsEcomStyles.reviewsContainer}>
        <Reviews />
      </div>
      <div className={supplementsEcomStyles.divider} />
      <div className={classNames(supplementsEcomStyles.faqContainer, 'supplements-ecom-faq')}>
        <FAQ />
      </div>
      <div className={supplementsEcomStyles.divider} />
      <div className={supplementsEcomStyles.footerContainer}>
        <Footer />
      </div>
      <HideChatWidget />
      <HideCookieConsentBanner />
    </>
  );
}
